import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Box from '../components/Box'
import { HighestImpactWorksite } from '../types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const WorksiteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const WorksiteCard = styled.div<{ isPrimary?: boolean }>`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 0.75rem;
  background-color: ${(props) =>
    props.isPrimary
      ? props.theme.palette.secondary.purple600
      : props.theme.palette.grey.grey100};
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  position: relative;
`

const BulletPoint = styled.div<{ color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  margin-right: 0.75rem;
  flex-shrink: 0;
`

const WorksiteInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const WorksiteName = styled(Text)<{ isPrimary?: boolean }>`
  color: ${(props) =>
    props.isPrimary
      ? props.theme.palette.white
      : props.theme.palette.text.primary};
  font-weight: 600;
`

const WorksiteDetails = styled(Text)<{ isPrimary?: boolean }>`
  color: ${(props) =>
    props.isPrimary
      ? props.theme.palette.white
      : props.theme.palette.text.secondary};
  font-size: 0.875rem;
`

const ReachInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`

const ReachPercentage = styled(Text)<{ isPrimary?: boolean }>`
  color: ${(props) =>
    props.isPrimary
      ? props.theme.palette.white
      : props.theme.palette.text.primary};
  font-weight: 700;
  font-size: 1.25rem;
`

const ReachLabel = styled(Text)<{ isPrimary?: boolean }>`
  color: ${(props) =>
    props.isPrimary
      ? props.theme.palette.white
      : props.theme.palette.text.secondary};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const SummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid ${(props) => props.theme.palette.grey.grey300};
  margin-top: 0.5rem;
`

const SummaryLabel = styled(Text)`
  color: ${(props) => props.theme.palette.text.primary};
  font-weight: 600;
`

const SummaryValue = styled(Text)`
  color: ${(props) => props.theme.palette.text.primary};
  font-weight: 700;
  font-size: 1.125rem;
`

interface HighestImpactWorksitesProps {
  worksites: HighestImpactWorksite[]
}

const HighestImpactWorksites: React.FC<HighestImpactWorksitesProps> = ({
  worksites,
}) => {
  const totalEmployees = worksites.reduce(
    (sum, worksite) => sum + worksite.totalEmployees,
    0
  )

  const getFocusType = (worksite: HighestImpactWorksite) => {
    if (
      worksite.transitReachPercentage > worksite.micromobilityReachPercentage
    ) {
      return 'Transit focused'
    } else {
      return 'Micromobility focused'
    }
  }

  const getBulletColor = (index: number) => {
    const colors = [
      '#FFFFFF', // White for primary
      '#2F8DFF', // Blue
      '#8B5CF6', // Purple
    ]
    return colors[index] || '#A3A0A0' // Default grey
  }

  return (
    <Box style={{ padding: '1.25rem' }}>
      <Container>
        <Header>
          <Text variant='h4' color='text.primary'>
            Highest Impact Worksites
          </Text>
          <Text variant='body2' color='text.secondary'>
            Sites with &gt;10% reach for alternative transportation modes
          </Text>
        </Header>

        <WorksiteList>
          {worksites.map((worksite, index) => (
            <WorksiteCard key={worksite.worksiteName} isPrimary={index === 0}>
              <BulletPoint color={getBulletColor(index)} />
              <WorksiteInfo>
                <WorksiteName variant='body1' isPrimary={index === 0}>
                  {worksite.worksiteName}
                </WorksiteName>
                <WorksiteDetails variant='body2' isPrimary={index === 0}>
                  {worksite.totalEmployees} employees • {getFocusType(worksite)}
                </WorksiteDetails>
              </WorksiteInfo>
              <ReachInfo>
                <ReachPercentage variant='h5' isPrimary={index === 0}>
                  {worksite.highestReachPercentage}%
                </ReachPercentage>
                <ReachLabel variant='caption' isPrimary={index === 0}>
                  Alt. Reach
                </ReachLabel>
              </ReachInfo>
            </WorksiteCard>
          ))}
        </WorksiteList>

        <SummaryContainer>
          <SummaryLabel variant='body1'>
            Total High-Impact Employees
          </SummaryLabel>
          <SummaryValue variant='h5'>{totalEmployees}</SummaryValue>
        </SummaryContainer>
      </Container>
    </Box>
  )
}

export default HighestImpactWorksites
